/**
 * t171u: the one money formatter. Pence in, en-GB pounds out with thousands separators: 150000 -> "£1,500.00".
 * Use it everywhere money is shown (task budget, project views, labour cost, premiums, offers, timesheets, reports,
 * fleet, sales); don't hand-roll `£${(p / 100).toFixed(2)}`. CSV exports for payroll keep plain numbers on purpose.
 */
const GBP = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
const GBP_WHOLE = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
const PLAIN = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export interface FormatGbpOptions {
  /** Shown for null/undefined (default an em dash). */
  empty?: string;
  /** Whole pounds, e.g. "£1,500" (targets, headlines). */
  whole?: boolean;
  /** Prefix positive amounts with "+" (differences, premiums). */
  signed?: boolean;
}

export function formatGBP(pence: number | null | undefined, opts: FormatGbpOptions = {}): string {
  if (pence == null || !Number.isFinite(pence)) return opts.empty ?? '—';
  const s = (opts.whole ? GBP_WHOLE : GBP).format(pence / 100);
  return opts.signed && pence > 0 ? `+${s}` : s;
}

/** A money input's text (the £ sits outside the box): 150000 -> "1,500.00"; null -> "". */
export const poundsInputText = (pence: number | null | undefined) => (pence == null || !Number.isFinite(pence) ? '' : PLAIN.format(pence / 100));

/** Parse what someone typed into a money input ("1,500", "£1500.5", " 12 ") to pence. "" -> null; not money -> undefined. */
export function parsePoundsInput(text: string): number | null | undefined {
  const t = text.replace(/[£,\s]/g, '');
  if (t === '') return null;
  if (!/^-?\d+(\.\d+)?$/.test(t)) return undefined;
  return Math.round(Number(t) * 100);
}
